import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  ResponsiveGrid,
  Table,
  Tabs,
  Text,
  Textarea,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { formatDate, formatDateTime } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  useDistinctDecisions,
  useMarkDistinct,
  usePersonDuplicates,
  useRevokeDistinct,
  type DistinctDecision,
  type DuplicatePair,
} from "../../../lib/queries/duplicates";
import type { PersonListItem } from "../../../lib/queries/persons";
import type { Person } from "../../../lib/queries/memberships";
import { duplicateReasonLabel } from "../../../lib/duplicateReasons";
import { EditPersonModal } from "../MembersRegistryPage/EditPersonModal";
import { PersonLink } from "../../../components/PersonDetail";
import styles from "./PersonDuplicatesPage.module.css";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

type TabId = "ke-kontrole" | "nejsou-totozni";

const fullName = (p: Person) => `${p.first_name} ${p.last_name}`;

function address(p: Person): string {
  const city = [p.postal_code, p.city].filter(Boolean).join(" ");
  return [p.street, city].filter(Boolean).join(", ");
}

function teamsWithSeason(p: PersonListItem): string {
  const teams = p.teams_label ?? "";
  if (!p.last_season) return teams;
  return teams ? `${teams} (${p.last_season})` : p.last_season;
}

// The rows compared side by side. Each value is what the cell READS, so two
// records that differ only in what is not shown never look different here.
const FIELDS: { label: string; value: (p: PersonListItem) => string; opensDetail?: boolean }[] = [
  { label: "Jméno", value: fullName, opensDetail: true },
  { label: "Datum narození", value: (p) => (p.birth_date ? formatDate(p.birth_date) : "") },
  { label: "Rodné číslo", value: (p) => p.birth_number },
  { label: "Adresa", value: address },
  { label: "ČNSMS ID", value: (p) => (p.cmf_id ? String(p.cmf_id) : "") },
  // The season beside the teams tells a club change (seasons follow one
  // another on one record) from one person registered twice across years
  // (one record ends where the other begins).
  { label: "Týmy (poslední sezóna)", value: teamsWithSeason },
  { label: "Přihlašovací e-mail", value: (p) => p.login_email },
];

function PersonColumn({
  person,
  other,
  onEdit,
}: {
  person: PersonListItem;
  other: PersonListItem;
  /** Absent for a reader who may not edit (Čtení všeho). */
  onEdit?: () => void;
}) {
  return (
    <div className={styles.person}>
      <DescriptionList>
        {FIELDS.map((f) => {
          const value = f.value(person);
          // A value the other record does not share is what tells the two
          // apart, so it is the one the eye should land on.
          const differs = value !== f.value(other);
          return (
            <DescriptionList.Item key={f.label} label={f.label}>
              {value === "" ? (
                <Text variant="muted">—</Text>
              ) : f.opensDetail ? (
                // The name opens the person detail dialog. Inline, so it
                // keeps the page's rule: bold only when it differs.
                differs ? (
                  <strong>
                    <PersonLink inline personId={person.id}>
                      {value}
                    </PersonLink>
                  </strong>
                ) : (
                  <PersonLink inline personId={person.id}>
                    {value}
                  </PersonLink>
                )
              ) : differs ? (
                <strong>{value}</strong>
              ) : (
                value
              )}
            </DescriptionList.Item>
          );
        })}
      </DescriptionList>
      {onEdit && (
        <Button size="sm" onClick={onEdit}>
          Upravit
        </Button>
      )}
    </div>
  );
}

/**
 * PersonDuplicatesPage lists Person records that look like one human
 * (service/duplicates) and lets the správce členské základny declare a pair
 * two different people. Merging is deliberately not here yet: a wrong merge
 * destroys history, so this step only finds and decides
 * (docs/rodne-cislo.md §6).
 */
export function PersonDuplicatesPage() {
  const { data: me } = useMe();
  // Čtení všeho reads both lists; deciding a pair and editing a person are
  // the membership office's.
  const readOnly = readsOnlyAsGlobalViewer(me, "members_admin");
  const [tab, setTab] = useState<TabId>("ke-kontrole");
  const pairs = usePersonDuplicates();
  const decisions = useDistinctDecisions();
  const markDistinct = useMarkDistinct();
  const revoke = useRevokeDistinct();
  const confirm = useConfirm();

  const [deciding, setDeciding] = useState<DuplicatePair | null>(null);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState<PersonListItem | null>(null);

  const strongCount = (pairs.data ?? []).filter((p) => p.strong).length;
  const tabs = useMemo(
    () => [
      {
        id: "ke-kontrole",
        label: "Ke kontrole",
        count: pairs.data?.length ?? 0,
        countVariant: strongCount > 0 ? ("warn" as const) : undefined,
      },
      { id: "nejsou-totozni", label: "Nejsou totožní", count: decisions.data?.length ?? 0 },
    ],
    [pairs.data, decisions.data, strongCount],
  );

  function closeDecide() {
    setDeciding(null);
    setReason("");
    markDistinct.reset();
  }

  const decisionColumns = useMemo<Column<DistinctDecision>[]>(
    () => [
      {
        key: "people",
        header: "Osoby",
        render: (d) => (
          <span>
            {fullName(d.a)} · {fullName(d.b)}
          </span>
        ),
      },
      { key: "reason", header: "Důvod", render: (d) => d.reason },
      {
        key: "decided",
        header: "Rozhodl(a)",
        render: (d) => (
          <div>
            {d.decided_by || <Text variant="muted">—</Text>}
            <Text as="p" variant="muted">
              {formatDateTime(d.created_at)}
            </Text>
          </div>
        ),
      },
      ...(readOnly
        ? []
        : [
          {
            key: "actions",
            header: "",
            render: (d: DistinctDecision) => (
              <Table.Actions>
                <Table.Action
                  appearance="button"
                  onClick={() =>
                    confirm({
                      title: "Vrátit mezi možné duplicity",
                      description: (
                        <>
                          Dvojice <strong>{fullName(d.a)}</strong> a <strong>{fullName(d.b)}</strong> se
                          znovu objeví ke kontrole.
                        </>
                      ),
                      confirmLabel: "Vrátit",
                      onConfirm: async () => {
                        await revoke.mutateAsync(d.id);
                      },
                    })
                  }
                >
                  Vrátit
                </Table.Action>
              </Table.Actions>
            ),
          },
          ]),
    ],
    [confirm, revoke, readOnly],
  );

  let pairsBody;
  if (pairs.isLoading) {
    pairsBody = <LoadingState label="Hledám možné duplicity…" />;
  } else if (pairs.error) {
    pairsBody = <ErrorState error={pairs.error} onRetry={() => pairs.refetch()} />;
  } else if ((pairs.data ?? []).length === 0) {
    pairsBody = (
      <EmptyState
        title="Žádné možné duplicity"
        description="V evidenci nejsou dvě osoby, které by vypadaly jako jeden člověk."
      />
    );
  } else {
    pairsBody = (pairs.data ?? []).map((p) => (
      <Card key={`${p.a.id}:${p.b.id}`}>
        <Card.Header
          title={p.strong ? "Pravděpodobně totožní" : "Možná totožní"}
          actions={
            !readOnly && <Button onClick={() => setDeciding(p)}>Nejsou totožní</Button>
          }
        />
        <Card.Content>
          <div className={styles.pair}>
            <div className={styles.reasons}>
              {p.reasons.map((r) => (
                <Badge key={r} variant={p.strong ? "warn" : "muted"}>
                  {duplicateReasonLabel(r)}
                </Badge>
              ))}
            </div>
            <ResponsiveGrid columns={2}>
              <PersonColumn person={p.a} other={p.b} onEdit={readOnly ? undefined : () => setEditing(p.a)} />
              <PersonColumn person={p.b} other={p.a} onEdit={readOnly ? undefined : () => setEditing(p.b)} />
            </ResponsiveGrid>
          </div>
        </Card.Content>
      </Card>
    ));
  }

  return (
    <>
      <AppTopbar
        crumbs={[{ label: "Členská základna", href: "/clenska-zakladna" }, { label: "Možné duplicity" }]}
      />
      <Page>
        <Page.Header
          title="Možné duplicity"
          subtitle="osoby, které vypadají jako jeden člověk; sloučení zatím aplikace neumí"
        />
        <Page.Content stack gap="lg">
          <Tabs
            aria-label="Možné duplicity"
            tabs={tabs}
            value={tab}
            onChange={(id) => setTab(id as TabId)}
          />
          {tab === "ke-kontrole" ? (
            pairsBody
          ) : (
            <Table
              framed
              columns={decisionColumns}
              rows={decisions.data ?? []}
              rowKey={(d) => d.id}
              isLoading={decisions.isLoading}
              error={decisions.error}
              onRetry={() => decisions.refetch()}
              empty={
                <EmptyState
                  title="Žádná rozhodnutí"
                  description="Dvojice označené jako dvě různé osoby se objeví tady."
                />
              }
            />
          )}
        </Page.Content>
      </Page>
      <ConfirmDialog
        open={deciding !== null}
        title="Nejsou totožní"
        confirmLabel="Označit jako dvě osoby"
        confirmDisabled={reason.trim() === ""}
        error={markDistinct.error}
        busy={markDistinct.isPending}
        onClose={closeDecide}
        onConfirm={() => {
          if (!deciding) return;
          markDistinct.mutate(
            { person_a_id: deciding.a.id, person_b_id: deciding.b.id, reason },
            { onSuccess: closeDecide },
          );
        }}
      >
        <p>
          <strong>{deciding && fullName(deciding.a)}</strong> a{" "}
          <strong>{deciding && fullName(deciding.b)}</strong> jsou dva různí lidé. Dvojice se
          přestane nabízet ke kontrole.
        </p>
        <Textarea
          label="Proč"
          value={reason}
          placeholder="například dvojčata, sourozenci, jiné rodné číslo"
          onChange={(e) => setReason(e.target.value)}
        />
      </ConfirmDialog>
      {editing && (
        <EditPersonModal person={editing} open onClose={() => setEditing(null)} />
      )}
    </>
  );
}
